/// <amd-module name="Repos/Views/PullRequest/PullRequestPolicyService" />
import { GitPullRequestMergeStrategy, GitPullRequestStatus } from "Repos/Common/Contracts/Git";
import { getTFSProject } from "Tfs/Platform/Page";
import {
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    IReadyableReadonlyObservableArray,
    ObservableArray,
    ObservableValue
} from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import {
    CheckEvaluation,
    IApproverCountPolicyEvaluation,
    IPullRequestCombinedPolicyEvaluation,
    IPullRequestPolicyEvaluation,
    IRequiredReviewersPolicyEvaluation,
    PolicyTypeId
} from "./Contracts/PullRequest";
import { IInternalPullRequestDataService, IPullRequestAutoCompleteData, PullRequestDataTypes } from "./PullRequestDataService";
import { getPullRequestViewService, IPullRequestViewService } from "./PullRequestViewService";
import { getPolicyClient } from "./RestClient/Policy";
import { getMostRestrictiveApprovalCountEvaluation, processCheckPolicies } from "./Util/Check";
import { getAutomaticReviewerPolicies, getBlockingPolicies, getMergePolicy, getUnsatisfiedPolicies } from "./Util/Policy";

export interface IPullRequestPolicyService extends IVssService {
    /**
     * Gets an observable that indicates whether or not there are any remaining
     * blocking (required) policies that have not been satisfied.
     */
    getHasBlockingPolicies(): IReadonlyObservableValue<boolean>;

    /**
     * Gets an observable that indicates whether or not there are any policies that
     * have not been successfully satisfied. False indicates that all policies are
     * either Approved or NotApplicable.
     */
    getHasUnsatisfiedPolicies(): IReadonlyObservableValue<boolean>;

    /**
     * Gets detailed checks to display in panel.
     */
    getCheckEvaluations(): IReadonlyObservableArray<CheckEvaluation>;

    /**
     * Gets an observable that indicates whether or not there is an active policy
     * that requires last iteration to be approved.
     */
    getIsNeedLastIterationApprove(): IReadonlyObservableValue<boolean>;

    /**
     * Gets the merge strategies that are disabled by policy for the target branch.
     */
    getDisallowedMergeStrategies(): IReadonlyObservableArray<GitPullRequestMergeStrategy>;

    /**
     * Gets the set of all policy evaluations for this pull request
     */
    getPolicies(): IReadyableReadonlyObservableArray<IPullRequestPolicyEvaluation>;

    /**
     * Gets the set of all required-reviewer policies
     */
    getAutomaticReviewerPolicies(): IReadonlyObservableArray<IPullRequestPolicyEvaluation>;

    /**
     * Requeue a policy evaluation (e.g. restart a build)
     */
    requeuePolicyEvaluation(evaluationId: string): void;
}

class PullRequestPolicyService extends VssService implements IPullRequestPolicyService {
    private dataService: IInternalPullRequestDataService;
    private prViewService: IPullRequestViewService;
    private hasBlockingPolicies = new ObservableValue(false);
    private hasUnsatisfiedPolicies = new ObservableValue(false);
    private isNeedLastIterationApprover = new ObservableValue(false);
    private checkEvaluations = new ObservableArray<CheckEvaluation>(undefined);
    private automaticReviewerPolicies = new ObservableArray<IRequiredReviewersPolicyEvaluation>();
    private disallowedMergeStrategies = new ObservableArray<GitPullRequestMergeStrategy>();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.dataService = pageContext.getService<IInternalPullRequestDataService>("IInternalPullRequestDataService");
        const policies = this.dataService.getPolicies();
        const statuses = this.dataService.getStatuses();
        const autoCompleteData = this.dataService.getAutoCompleteData();

        this.prViewService = getPullRequestViewService(pageContext);
        const completionStatus = this.prViewService.getCompletionStatus();

        const setAutoCompleteIgnore = (ignore: boolean, configurationIds: readonly number[]): void => {
            this.prViewService.setAutoCompleteIgnore(ignore, configurationIds);
        };

        const policyUpdateSubscriber = () => {
            this.updatePolicies(policies.value, statuses.value, autoCompleteData.value, setAutoCompleteIgnore);
        };

        policies.subscribe(policyUpdateSubscriber);
        statuses.subscribe(policyUpdateSubscriber);
        autoCompleteData.subscribe(policyUpdateSubscriber);
        completionStatus.subscribe(policyUpdateSubscriber);
    }

    public getDisallowedMergeStrategies(): IReadonlyObservableArray<GitPullRequestMergeStrategy> {
        return this.disallowedMergeStrategies;
    }

    public getHasBlockingPolicies(): IReadonlyObservableValue<boolean> {
        return this.hasBlockingPolicies;
    }

    public getHasUnsatisfiedPolicies(): IReadonlyObservableValue<boolean> {
        return this.hasUnsatisfiedPolicies;
    }

    public getPolicies(): IReadyableReadonlyObservableArray<IPullRequestPolicyEvaluation> {
        return this.dataService.getPolicies();
    }

    public getAutomaticReviewerPolicies(): IReadonlyObservableArray<IRequiredReviewersPolicyEvaluation> {
        return this.automaticReviewerPolicies;
    }

    public getCheckEvaluations(): IReadonlyObservableArray<CheckEvaluation> {
        return this.checkEvaluations;
    }

    public getIsNeedLastIterationApprove(): IReadonlyObservableValue<boolean> {
        return this.isNeedLastIterationApprover;
    }

    public requeuePolicyEvaluation(evaluationId: string) {
        const project = getTFSProject(this.pageContext)!;
        getPolicyClient(this.pageContext)
            .requeuePolicyEvaluation(project.id, evaluationId)
            .then(() => this.dataService.refreshPullRequestData(PullRequestDataTypes.policies | PullRequestDataTypes.autoComplete));
    }

    // Combine similar policies which should appear only once in the UI. For example, you wouldn't want
    // three "Comments must be resolved" policies showing up. Instead combine them into one policy, from
    // the user's perspective.

    // We do this by finding the most restrictive policy.

    private combineAllPolicyTypes(policies: IPullRequestPolicyEvaluation[]) {
        policies = this.combinePolicyType(policies, PolicyTypeId.commentResolution, isBlockingCombiner);
        policies = this.combinePolicyType(policies, PolicyTypeId.workItemLinking, isBlockingCombiner);
        policies = this.combinePolicyType(policies, PolicyTypeId.approvalCount, approvalCountCombiner);

        return policies;
    }

    private combinePolicyType<TPolicy extends IPullRequestPolicyEvaluation>(
        policies: IPullRequestPolicyEvaluation[],
        policyTypeId: string,
        combiner: (policies: TPolicy[]) => TPolicy
    ): IPullRequestPolicyEvaluation[] {
        const policiesToCombine = policies.filter(p => p.policyType.id === policyTypeId) as TPolicy[];

        if (policiesToCombine.length <= 1) {
            return policies;
        }

        const combinedConfigurationIds = policiesToCombine.map(p => p.configurationId);

        const combinedPolicy: IPullRequestCombinedPolicyEvaluation = {
            ...combiner(policiesToCombine),
            evaluationId: "",
            configurationId: 0,
            configurationIds: combinedConfigurationIds
        };

        policies = policies.filter(p => p.policyType.id !== policyTypeId);
        policies.push(combinedPolicy);

        return policies;
    }

    private updatePolicies(
        policies: IPullRequestPolicyEvaluation[],
        statuses: GitPullRequestStatus[],
        autoCompleteData: IPullRequestAutoCompleteData | undefined,
        setAutoCompleteIgnore: (ignore: boolean, configurationIds: readonly number[]) => void
    ): void {
        // Combine policy types where appropriate
        policies = this.combineAllPolicyTypes(policies);

        this.automaticReviewerPolicies.value = getAutomaticReviewerPolicies(policies);

        this.hasBlockingPolicies.value = getBlockingPolicies(policies).length > 0;
        this.hasUnsatisfiedPolicies.value = getUnsatisfiedPolicies(policies).length > 0;
        this.isNeedLastIterationApprover.value = !!getMostRestrictiveApprovalCountEvaluation(this.getPolicies().value)?.needsVoteOnLastIteration;

        const prBasicData = this.dataService.getPullRequestBasicData().value;

        this.checkEvaluations.value = processCheckPolicies(
            policies,
            statuses,
            autoCompleteData,
            this.pageContext,
            prBasicData.pullRequest,
            prBasicData.latestIterationId,
            evaluationId => this.requeuePolicyEvaluation(evaluationId),
            setAutoCompleteIgnore
        );

        const disallowedStrategies = [];

        const mergePolicy = getMergePolicy(policies);
        if (mergePolicy) {
            !mergePolicy.allowNoFastForward && disallowedStrategies.push(GitPullRequestMergeStrategy.NoFastForward);
            !mergePolicy.allowRebase && disallowedStrategies.push(GitPullRequestMergeStrategy.Rebase);
            !mergePolicy.allowRebaseMerge && disallowedStrategies.push(GitPullRequestMergeStrategy.RebaseMerge);
            !mergePolicy.allowSquash && disallowedStrategies.push(GitPullRequestMergeStrategy.Squash);
        }

        this.disallowedMergeStrategies.value = disallowedStrategies;
    }
}

function isBlockingCombiner(policies: IPullRequestPolicyEvaluation[]): IPullRequestPolicyEvaluation {
    // Simplest case: required policies are "stricter" than optional policies.
    return policies.find(p => p.isBlocking) || policies[0];
}

function approvalCountCombiner(policies: IApproverCountPolicyEvaluation[]): IApproverCountPolicyEvaluation {
    let strictestPolicy = policies[0];

    let allowDownvotes = policies[0].allowDownvotes;

    for (let i = 1; i < policies.length; ++i) {
        if (!policies[i].allowDownvotes) {
            allowDownvotes = false;
        }

        if (
            policies[i].minimumApproverCount + (policies[i].creatorVoteCounts ? 0 : 0.5) >
            strictestPolicy.minimumApproverCount + (strictestPolicy.creatorVoteCounts ? 0 : 0.5)
        ) {
            strictestPolicy = policies[i];
        }
    }

    strictestPolicy.allowDownvotes = allowDownvotes;

    return strictestPolicy;
}

const serviceName = "IPullRequestPolicyService";

export function getPullRequestPolicyService(pageContext: IVssPageContext): IPullRequestPolicyService {
    return pageContext.getService<IPullRequestPolicyService>(serviceName);
}

Services.add(serviceName, { serviceFactory: PullRequestPolicyService });
